import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';

/**
 * Modal for inviting a new user. Replaces the legacy create-mode of
 * UserModal with a template-card picker.
 *
 * Flow: name → email → pick one of 5 template cards → "שלח הזמנה".
 * Backend applies the template's permissions + seeds a default
 * email channel pre-subscribed to the template's alert types.
 *
 * The card-based picker keeps the surface visual for non-technical
 * managers — every template carries a short Hebrew description so
 * the manager understands what they're choosing before they choose.
 */
function InviteUserModal({
  usersBase,
  templatesEndpoint,
  allowManagerCreation = false,
  onClose,
  onCreated,
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [templates, setTemplates] = useState([]);
  const [pickedKey, setPickedKey] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(templatesEndpoint)
      .then(res => setTemplates(res.data.templates || []))
      .catch(() => setTemplates([]));
  }, [templatesEndpoint]);

  // ``allowManagerCreation`` is admin-side only; on the customer side
  // we currently hide the "primary_manager" card from the picker so a
  // manager can't accidentally invite a peer with full powers. The
  // admin mirror passes true to surface every template.
  const visibleTemplates = allowManagerCreation
    ? templates
    : templates.filter(t => t.key !== 'primary_manager');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!pickedKey) {
      setError('יש לבחור תפקיד');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(usersBase, {
        name, email, template_key: pickedKey,
      });
      onCreated && onCreated(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה ביצירת המשתמש');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <>
      <div className="modal-overlay" onClick={onClose} />
      <form
        className="modal"
        onSubmit={handleSubmit}
        style={{
          position: 'fixed',
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 101,
          width: 'min(640px, calc(100vw - 24px))',
          maxHeight: '92vh', overflowY: 'auto',
        }}
      >
        <h2>הזמנת משתמש חדש</h2>

        <div className="form-group">
          <label>שם מלא</label>
          <input value={name} onChange={e => setName(e.target.value)} required autoFocus />
        </div>
        <div className="form-group">
          <label>אימייל</label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            dir="ltr"
            style={{ textAlign: 'left' }}
          />
        </div>

        <div style={{ marginTop: '1.1rem' }}>
          <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem', fontSize: '0.9rem' }}>
            תפקיד
          </label>
          <p style={{ color: '#6b7280', fontSize: '0.78rem', margin: '0 0 0.75rem' }}>
            בחר תפקיד מתאים — ניתן לערוך הרשאות ספציפיות בפרופיל המשתמש לאחר ההזמנה.
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: '0.6rem',
          }}>
            {visibleTemplates.map(t => (
              <TemplateCard
                key={t.key}
                template={t}
                picked={pickedKey === t.key}
                onPick={() => setPickedKey(t.key)}
              />
            ))}
          </div>
        </div>

        {error && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fecaca',
            color: '#b91c1c', padding: '0.5rem 0.75rem',
            borderRadius: '6px', marginTop: '0.85rem', fontSize: '0.8rem',
          }}>
            {error}
          </div>
        )}
        <div className="modal-actions">
          <button type="submit" className="btn btn-primary" disabled={saving || !pickedKey}>
            {saving ? 'שולח...' : 'שלח הזמנה'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            ביטול
          </button>
        </div>
      </form>
    </>,
    document.body
  );
}


function TemplateCard({ template, picked, onPick }) {
  return (
    <button
      type="button"
      onClick={onPick}
      style={{
        textAlign: 'right',
        background: picked ? '#1a1a2e' : '#fff',
        color: picked ? '#fff' : '#1a1a2e',
        border: picked ? '2px solid #1a1a2e' : '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '0.85rem',
        cursor: 'pointer',
        fontFamily: 'Heebo, sans-serif',
        transition: 'border-color 0.1s, background 0.1s',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.35rem',
      }}
    >
      <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>
        {template.label}
      </div>
      <div style={{
        fontSize: '0.78rem',
        color: picked ? '#cbd5e1' : '#6b7280',
        lineHeight: 1.45,
      }}>
        {template.description}
      </div>
    </button>
  );
}


export default InviteUserModal;
